import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import * as customStore from '../api/customItemsStore';
// Search will also include live server popups to be consistent across browsers

export default function SearchPopup({ isOpen, onClose, role }) {
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all'); // 'all' | 'serial' | 'film'
    const [results, setResults] = useState([]);
    const [customItems, setCustomItems] = useState([]); // user-defined items (readonly)
    const [serverPopups, setServerPopups] = useState([]); // live popups from API
    const [busy, setBusy] = useState(false);
    const [popupStyle, setPopupStyle] = useState(null);
    const containerRef = useRef(null);
    
    // load custom items for backward compatibility (readonly)
    useEffect(() => {
        let mounted = true;
        async function load() {
            const items = await customStore.getItems();
            if (mounted) setCustomItems(items || []);
        }
        load();
        return () => { mounted = false; };
    }, []);

    // refresh custom items & server popups whenever search catalog changes (e.g., after Admin save)
    useEffect(() => {
        const onSearchChanged = async () => {
            try {
                const items = await customStore.getItems();
                setCustomItems(items || []);
                // refresh server popups too
                try {
                    const r = await fetch('/api/popups', { cache: 'no-store' });
                    if (r.ok) {
                        const j = await r.json();
                        setServerPopups(Array.isArray(j) ? j : []);
                    }
                } catch {}
            } catch {}
        };
        window.addEventListener('search:changed', onSearchChanged);
        return () => window.removeEventListener('search:changed', onSearchChanged);
    }, []);

    // helper: normalize arbitrary URL or input into a path used by pagesStore
    function normalizeToPath(u) {
        if (!u) return '';
        try {
            if (/^https?:\/\//i.test(u)) {
                const parsed = new URL(u);
                return (parsed.pathname || '/') + (parsed.search || '');
            }
        } catch (e) {
            // fallthrough
        }
        return u.startsWith('/') ? u : '/' + u;
    }

    useEffect(() => {
        if (isOpen) {
            // reload items on open to ensure we have the latest saved entries + live server popups
            (async () => {
                try { const items = await customStore.getItems(); setCustomItems(items || []); } catch {}
                try {
                    setBusy(true);
                    const r = await fetch('/api/popups', { cache: 'no-store' });
                    if (r.ok) {
                        const j = await r.json();
                        setServerPopups(Array.isArray(j) ? j : []);
                    }
                } catch {} finally { setBusy(false); }
            })();
            setTimeout(() => {
                const input = containerRef.current?.querySelector('input');
                if (input) input.focus();
            }, 50);
        } else {
            setQuery('');
            setResults([]);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose && onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    useEffect(() => {
        function handleOutside(e) {
            if (!isOpen) return;
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onClose && onClose();
            }
        }
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [isOpen, onClose]);

    const strip = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const deriveType = (p) => {
        const raw = (p?.contentType || p?.type || p?.category || '').toString().toLowerCase();
        if (raw.includes('film') || raw.startsWith('fi') || raw === 'movie') return 'film';
        if (raw.includes('seri') || raw === 'series' || raw === 'serial') return 'serial';
        // Infer from episodes count if ambiguous
        const eps = Array.isArray(p?.episodes) ? p.episodes : [];
        return eps.length <= 1 ? 'film' : 'serial';
    };

    const doSearch = () => {
        const qRaw = (query || '').trim();
        const q = strip(qRaw);
        const found = [];
        const seen = new Set();
        const isPriv = role === 'Majitel' || role === 'Admin' || role === 'Moderátor' || role === 'Moderator';

        // Pokud není dotaz, nic nevracíme (prázdné výsledky)
        if (!q) {
            setResults([]);
            return;
        }

        // 1) Hledat v živých popupech ze serveru (sdílený zdroj pravdy)
        const serverLinkSet = new Set();
        const popups = Array.isArray(serverPopups) ? serverPopups : [];
        for (let i = 0; i < popups.length && found.length < 40; i++) {
            const p = popups[i];
            if (!p || !p.title) continue;
            const type = deriveType(p);
            if (filter !== 'all' && filter !== type) continue;

            const title = strip(p.title || '');
            const orig = strip(p.originalTitle || p.origTitle || '');
            const year = strip(String(p.year || ''));
            const tags = Array.isArray(p.tags) ? strip(p.tags.join(' ')) : '';
            const genres = Array.isArray(p.genres) ? strip(p.genres.join(' ')) : '';
            const aliases = Array.isArray(p.aliases) ? strip(p.aliases.join(' ')) : '';
            let epTitles = '';
            try {
                if (Array.isArray(p.episodes)) {
                    // include first N episode titles for searchability without exploding size
                    epTitles = strip(p.episodes.slice(0, 50).map(e => e && e.title ? e.title : '').join(' '));
                }
            } catch {}
            const hay = `${title} ${orig} ${year} ${tags} ${genres} ${aliases} ${epTitles}`;
            if (!hay.includes(q)) continue;

            const link = type === 'film'
                ? `/filmy?preview=${encodeURIComponent(String(p.id))}`
                : `/serialy?preview=${encodeURIComponent(String(p.id))}`;
            const key = `server:${p.id}`;
            if (seen.has(key)) continue;
            seen.add(key);
            serverLinkSet.add(normalizeToPath(link));
            found.push({
                title: p.title,
                snippet: `${type === 'film' ? 'Film' : 'Seriál'}${p.year ? ' • ' + String(p.year) : ''}`,
                img: p.searchPoster || p.listThumb || p.heroImage || null,
                isCustom: true, // reuse same click handler path for url navigation
                url: link,
            });
        }

        // 2) Hledat v customItems (lokální položky) — ale pro Admin/Majitel/Moderátor skryjeme duplicitní či vše
        if (!isPriv) {
            for (let i = 0; i < customItems.length && found.length < 40; i++) {
                const it = customItems[i];
                if (!it || !it.title) continue;
                if (filter !== 'all' && it.type !== filter) continue;

                const title = strip(it.title || '');
                const urlStr = it.url || '';
                const urlNorm = normalizeToPath(urlStr);

                // Pokud už existuje stejný link ze serveru, přeskoč (aby nebyly duplicity)
                if (serverLinkSet.has(urlNorm)) continue;

                const urlHay = strip(urlStr || '');
                if (title.includes(q) || urlHay.includes(q)) {
                    const key = `custom:${it.title}:${urlStr}`;
                    if (seen.has(key)) continue;
                    seen.add(key);
                    found.push({
                        title: it.title,
                        snippet: (it.year ? String(it.year) : (it.url || it.title)),
                        img: it.img || null,
                        isCustom: true,
                        url: it.url || null,
                    });
                }
            }
        }

        setResults(found);
    };
    
    // live search while typing / when filter or items change
    useEffect(() => {
        doSearch();
    }, [query, filter, customItems]);

    const handleSubmit = (e) => {
        e && e.preventDefault();
        doSearch();
    };

    // Přidávání/vytváření stránek přes search je zakázané – pouze otevírá popup

    // při mazání položky: potvrzení, odstranit z custom store a pokud existovala cílová stránka -> smazat ji také
    const removeCustomItem = async () => {};

    // useLayoutEffect so position is measured before paint — compute once on open (no scroll tracking)
    useLayoutEffect(() => {
        if (!isOpen) {
            setPopupStyle(null);
            return;
        }
        let mounted = true;
        const updatePos = () => {
            try {
                const img = document.querySelector('img[src*="HLEDAT.png"], img[src*="hledat.png"]');
                if (!img) {
                    if (mounted) setPopupStyle(null);
                    return;
                }
                const r = img.getBoundingClientRect();
                // use viewport coordinates so popup is fixed in place (won't follow page scroll)
                const top = Math.round(r.bottom);
                const left = Math.round(r.left);
                if (mounted) setPopupStyle({ position: 'fixed', top: `${top}px`, left: `${left}px` });
            } catch (err) {
                if (mounted) setPopupStyle(null);
            }
        };
        updatePos();
        window.addEventListener('resize', updatePos);
        return () => {
            mounted = false;
            window.removeEventListener('resize', updatePos);
        };
    }, [isOpen]);

    return isOpen ? (
        <Overlay>
            {/* apply opacity so popup isn't visible until positioned */}
            <Popup
                ref={containerRef}
                wide={role === 'Majitel' || role === 'Admin'}
                style={{ ...(popupStyle || {}), opacity: popupStyle ? 1 : 0, transition: 'opacity 120ms ease' }}
            >
                <HeaderRow>
                    <Title>Hledat</Title>
                    <CloseButton onClick={onClose}>✕</CloseButton>
                </HeaderRow>
                <form onSubmit={handleSubmit}>
                    <SearchRow>
                        <SearchInput
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Napiš název, např. Ninjago..."
                            onMouseDown={(e) => { e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); e.currentTarget.focus(); }}
                        />
                    </SearchRow>
                </form>
                <FiltersRow>
                    <FilterLabel>
                            <input type="radio" name="f" checked={filter === 'all'} onChange={() => setFilter('all')} />
                            <FilterPill>Všechno</FilterPill>
                        </FilterLabel>
                        <FilterLabel>
                            <input type="radio" name="f" checked={filter === 'serial'} onChange={() => setFilter('serial')} />
                            <FilterPill>Seriály</FilterPill>
                        </FilterLabel>
                        <FilterLabel>
                            <input type="radio" name="f" checked={filter === 'film'} onChange={() => setFilter('film')} />
                            <FilterPill>Filmy</FilterPill>
                        </FilterLabel>
                </FiltersRow>

                {/* Custom items editor (zobrazuje se jen Majitel / Admin) */}
                {/* Editor vlastních položek odstraněn – search pouze otevírá popup */}

                <ResultsArea admin={(role === 'Majitel' || role === 'Admin')}>
                    {results.length === 0 ? (
                        <EmptyState>Žádné výsledky. Zkusit jiné klíčové slovo.</EmptyState>
                    ) : (
                        results.map((r, i) => (
                            <ResultItem key={r.isCustom ? `custom-${i}` : i} onClick={() => {
                                 try {
                                     if (r.isCustom && r.url) {
                                         const url = new URL(r.url, window.location.href);
                                         if (url.searchParams && url.searchParams.has('preview')) {
                                             // open overlay without reload
                                             window.history.pushState({}, '', url.pathname + url.search);
                                             const id = url.searchParams.get('preview');
                                             window.dispatchEvent(new CustomEvent('preview:open', { detail: { id } }));
                                         } else {
                                             window.location.href = r.url;
                                         }
                                     } else if (!r.isCustom) {
                                         const el = document.querySelector(r.nodePath) || null;
                                         if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                     }
                                 } catch (err) {}
                                 onClose && onClose();
                            }}>
                                <Thumb>
                                    <img src={r.img || '/images/placeholder-thumb.png'} alt="" />
                                </Thumb>
                                <ResultText>
                                    <ResultTitle>{r.title || r.snippet}</ResultTitle>
                                    {r.snippet && (
                                        <ResultSnippet>{r.snippet}</ResultSnippet>
                                    )}
                                </ResultText>
                             </ResultItem>
                         ))
                     )}
                </ResultsArea>
            </Popup>
        </Overlay>
    ) : null;
}

/* Styled components */
const Overlay = styled.div`
    position: fixed;
    top: 70px;
    left: 34px;
    right: 34px;
    z-index: 1200000;
    display: flex;
    justify-content: center;
    pointer-events: auto;
`;

const Popup = styled.div`
    width: ${props => props.wide ? '760px' : '520px'};
    max-width: calc(100% - 40px);
    background: #071014; /* site popup card color */
    color: #fff;
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    border: 1px solid rgba(255,255,255,0.04);
    transition: transform .12s ease, opacity .12s ease;
`;
 
const HeaderRow = styled.div`
    display:flex;
    justify-content:space-between;
    align-items:center;
    margin-bottom:10px;
`;

const Title = styled.div`
    font-weight:800;
    font-size:18px;
    color: #e6eef8;
`;

const CloseButton = styled.button`
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.04);
    color:#fff;
    width:34px;
    height:34px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    border-radius:8px;
    cursor:pointer;
    transition: transform .12s ease, background .12s ease;
    &:hover { transform: translateY(-2px); background: rgba(255,255,255,0.06); }
`;

const SearchRow = styled.div`
    display:flex;
    gap:8px;
    margin-bottom:12px;
    align-items:center;
`;

const SearchInput = styled.input`
    flex:1;
    padding:12px 14px;
    border-radius:10px;
    border:1px solid rgba(255,255,255,0.04);
    background:#0f1720;
    color:#fff;
    font-size:15px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.02);
    transition: border-color .12s, box-shadow .12s, transform .12s;
    &:focus {
        outline: none;
        border-color: #2b2f33;
        box-shadow: 0 6px 18px rgba(2,6,23,0.6);
        transform: translateY(-1px);
    }
`;

const FiltersRow = styled.div`
    display:flex;
    gap:8px;
    margin-bottom:14px;
    align-items:center;
`;

const FilterLabel = styled.label`
    font-size:13px;
    display:inline-flex;
    align-items:center;
    gap:8px;
    padding:6px 8px;
    border-radius:6px;
    background:transparent;
    color:#ddd;
    cursor:pointer;
    transition: background .12s, color .12s, border-color .12s;

    input {
        margin:0;
        width:14px;
        height:14px;
        -webkit-appearance:none;
        appearance:none;
        /* hide native radio; we'll style a pill next to it */
        position: absolute;
        opacity: 0;
        pointer-events: none;
    }

    input:focus {
        outline:none;
        box-shadow:none;
        border-color:#888;
    }

    /* Make hover target the pill for a smoother visual effect */
    &:hover {
        background: transparent;
        color: #fff;
    }
    &:hover > span {
        background: rgba(255,255,255,0.04);
        color: #fff;
        transform: translateY(-1px);
        box-shadow: 0 10px 24px rgba(2,6,23,0.45);
        border-color: rgba(255,255,255,0.06);
    }
`;

const FilterPill = styled.span`
    display:inline-flex;
    align-items:center;
    justify-content:center;
    padding:8px 12px;
    border-radius:999px;
    border:1px solid rgba(255,255,255,0.06);
    background: transparent;
    color: #dfe9f8;
    font-size:13px;
    min-width: 72px;
    text-align: center;
    transition: background .14s ease, color .12s ease, transform .12s ease, border-color .12s ease;
    cursor: pointer;

    /* when the preceding input is checked, highlight the pill */
    input:checked + & {
        background: #e50914;
        color: #fff;
        border-color: #e50914;
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(229,9,20,0.12);
    }
`;

/* custom items editor */
const CustomEditor = styled.div`
    margin-bottom:12px;
    border-top:1px solid #222;
    padding-top:12px;
`;

const EditorRow = styled.div`
     display:flex;
     gap:8px;
     margin-bottom:8px;
     flex-wrap:wrap;
 `;

const SmallInput = styled.input`
    padding:8px;
    border-radius:6px;
    border:1px solid #333;
    background:#222;
    color:#fff;
    font-size:13px;
    &:focus { outline:none; box-shadow:none; border-color:#444; }
`;

const TypeSelect = styled.select`
    padding:8px;
    border-radius:6px;
    border:1px solid #333;
    background:#222;
    color:#fff;
    font-size:13px;
    &:focus { outline:none; box-shadow:none; border-color:#444; }
`;

const AddBtn = styled.button`
    padding:8px 10px;
    border-radius:6px;
    border:none;
    background:#2a8f4a;
    color:#fff;
    cursor:pointer;
`;

const CustomList = styled.div`
    display:flex;
    flex-direction:column;
    gap:8px;
    max-height: 180px; /* umožní scrollovat při větším počtu položek */
    overflow: auto;
    padding-right: 6px; /* prostor pro scrollbar */
`;

const SmallNote = styled.div`
    color:#bbb;
    font-size:13px;
`;

const CustomItem = styled.div`
    display:flex;
    align-items:center;
    gap:6px;
    background:#0f0f0f;
    padding:8px;
    border-radius:6px;
`;

const ItemThumb = styled.div`
    width:36px;
    height:36px;
    flex:0 0 36px;
    img { width:100%; height:100%; object-fit:cover; border-radius:4px; }
`;

const ItemText = styled.div`
    flex:1;
`;

const DelBtn = styled.button`
    background:transparent;
    border:1px solid #444;
    color:#fff;
    padding:6px 8px;
    border-radius:6px;
    cursor:pointer;
`;

const ResultsArea = styled.div`
    /* Show up to 4 result items, then enable scrolling */
    max-height: 344px; /* ~4 items (64px thumb + padding) + gaps */
    overflow:auto;
    display:flex;
    flex-direction:column;
    gap:8px;

    /* Grey scrollbar styling to match PlayPage episode list */
    scrollbar-width: auto;
    scrollbar-color: rgba(136,136,136,0.75) transparent;
    scrollbar-gutter: stable;

    &::-webkit-scrollbar { width: 14px; height: 14px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: rgba(136,136,136,0.75); border-radius: 8px; border: 3px solid transparent; }
    &::-webkit-scrollbar-button,
    &::-webkit-scrollbar-button:single-button,
    &::-webkit-scrollbar-button:start,
    &::-webkit-scrollbar-button:end,
    &::-webkit-scrollbar-button:vertical,
    &::-webkit-scrollbar-button:vertical:decrement,
    &::-webkit-scrollbar-button:vertical:increment,
    &::-webkit-scrollbar-button:single-button:vertical { display: none; width: 0; height: 0; }
    &::-webkit-scrollbar-corner { background: transparent; }
`;

const EmptyState = styled.div`
    color:#bbb;
    padding:18px;
`;

const ResultItem = styled.div`
    display:flex;
    align-items:center;
    gap:10px;
    background:#0f0f0f;
    padding:8px;
    border-radius:6px;
    cursor:pointer;
    min-height:84px; /* ensure consistent height so 4 items fit neatly */
    &:hover { background:#1a1a1a; }
`;

const ResultText = styled.div`
    flex:1;
    padding-right:8px;
`;

const ResultTitle = styled.div`
    font-weight:600;
    font-size:16px;
`;

const ResultSnippet = styled.div`
    font-size:12px;
    color:#bbb;
    margin-top:4px;
`;

const Thumb = styled.div`
    width:64px;
    height:64px;
    flex:0 0 64px;
    margin-right:8px;
    img {
        width:100%;
        height:100%;
        object-fit:cover;
        border-radius:4px;
    }
`;
